import { AuthenticationResult } from "@feathersjs/authentication";
import { FeathersService } from "@feathersjs/feathers";
import { Location } from "history";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import i18next from "./../i18n";
import { Services, UserType, XLSX_UserRow, SessionData } from "types";
import { RoutingStore } from ".";
import client from "../feathers";
import { Course, User } from "../models";
import { BaseStore } from "./baseStore";

type AccountActionResponse = "success" | "error" | "no_token" | "invalid_token";
type ForgotPasswordRequestResponse = "no_account_found";
type ResetPasswordRequestResponse = "no_password";

export class AuthStore extends BaseStore {
  protected readonly invite_service: FeathersService<any> = client.service(Services.Invite);
  protected readonly account_service: FeathersService<any> = client.service(Services.Account);
  protected readonly user_service: FeathersService<any> = client.service(Services.Users);

  user: User | null  = null;  // Logged in user
  tab: number | null = null;
  error_message: string | null = null;
  isAuthenticating             = true;

  constructor(public routingStore: RoutingStore) {
    super();
    
    // Note: This will not take care of async calls. 
    // So everything that might be called async has to be wrapped in runInAction() to avoid warnings
    makeObservable(this, {
      user:             observable,
      tab:              observable,
      error_message:    observable,
      isAuthenticating: observable,

      setAuth:        action,
      reAuthenticate: action,
      login:          action,
      logout:         action,

      getCurUser:     computed,
      isLoggedIn:     computed,
      getCurUserType: computed,
    });

    // Check JWT to re-log in if possible
    this.reAuthenticate();

    // Get the notification about 
    client.service(Services.Session).on('connect', (data: SessionData) =>  {
      runInAction(() => ( this.tab = data.total_sessions ));
    });

    // Logout coming 
    client.service(Services.Session).on('logout', () =>  {
      console.log('you are logged out!')
      //this.logout();
    });

  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init = () => {
    // init
  };
  clear = () => {
    // clear
  };
  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/
  get getCurUserID(): number {
    return this.getCurUser?.id;
  }
  get getCurUser(): User {
    return this.user;
  }
  get getCurUserType(): UserType {
    return this.user?.user_type;
  }
  get isLoggedIn(): boolean {
    return Boolean(this.user);
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/

  setAuth = runInAction(() => (res: AuthenticationResult) => {
    if ("user" in res) {
      this.user = User.create(res.user);
      i18next.changeLanguage(this.user.language || "en");
    } else {
      console.error(res);
    }
    this.isAuthenticating = false;
  });

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  reAuthenticate = async (): Promise<void> => {
    try {
      this.isAuthenticating = true;
      const res: AuthenticationResult = await client.reAuthenticate();
      this.setAuth(res);

      if (this.user.user_type === UserType.Manager)
        await this.user.setCompany();

    } catch (err) {
      console.error(err);
    }
    runInAction(() => (this.isAuthenticating = false));
  };

  login = async (email: string, password: string): Promise<boolean> => {
    runInAction(() => (this.error_message = ""));
    if (!email || !password) {
      runInAction(
        () => (this.error_message = "Login requires an email and a password")
      );
      return false;
    }
    try {
      this.isAuthenticating = true;
      runInAction(() => (this.isAuthenticating = true));
      const res: AuthenticationResult = await client.authenticate({
        strategy: "local",
        email,
        password
      });

      this.setAuth(res);

      if (this.user.user_type === UserType.Manager) {
        await this.user.setCompany();
      }

      return true;
    } catch (err) {
      console.error(err);
      if (err.message === "Invalid login") {
        runInAction(() => (this.error_message = i18next.t("invalid_login")));
      }
    }
    runInAction(() => (this.isAuthenticating = false));
    return false;
  };

  signInBankID = async (): Promise<boolean> => {
    runInAction(() => (this.error_message = ""));
    try {
      this.isAuthenticating = true;
      runInAction(() => (this.isAuthenticating = true));
      const callbackUrl: string = await client.service(Services.BankID).auth(null);
      window.location.href = callbackUrl
      return true;
    } catch (err) {
      console.error(err);
      if (err.message === "Invalid login") {
        runInAction(() => (this.error_message = i18next.t("invalid_login")));
      }
    }
    runInAction(() => (this.isAuthenticating = false));
    return false;
  };

  validateBankIdLoginCallback = async (session: string): Promise<boolean> => {
    runInAction(() => (this.error_message = ""));
    try {
      this.isAuthenticating = true;
      runInAction(() => (this.isAuthenticating = true));
      const accessToken: string = await client.service(Services.BankID).login(session);

      if (accessToken) {
        client.get("storage").setItem("feathers-jwt", accessToken);
        this.reAuthenticate();
      }
      if(!accessToken)  {
        runInAction(() => (this.error_message = i18next.t("invalid_login")));
      }
      return true;
    } catch (err) {
      console.error(err);
      if (err.message === "Invalid login") {
        runInAction(() => (this.error_message = i18next.t("invalid_login")));
      }
    }
    runInAction(() => (this.isAuthenticating = false));
    return false;
  };

  validateBankIdConnectCallback = async (session: string): Promise<boolean> => {
    runInAction(() => (this.error_message = ""));
    try {
      // runInAction(() => (this.isAuthenticating = true));
      const result: string = await client.service(Services.BankID).validate(session);
      this.user.set('personal_number', result, false);
      if(!result) {
        runInAction(() => (this.error_message = i18next.t("invalid_validation")));
        return false;
      }
      return true;
    } catch (err) {
      console.error(err);
      runInAction(() => (this.error_message = i18next.t("invalid_validation")));
      return false;
    }
  };

  unlinkBankId = async (): Promise<boolean> => {
    runInAction(() => (this.error_message = ""));
    try {
      const result = await client.service(Services.BankID).unlink(null);
      if(result) this.user.set('personal_number', null, false)
      return true;
    } catch (err) {
      console.error(err);
    }
    return false;
  };

  connectBankId = async (): Promise<boolean> => {
    runInAction(() => (this.error_message = ""));
    try {
      const callbackUrl: string = await client.service(Services.BankID).associate(null);
      window.location.href = callbackUrl
      return true;
    } catch (err) {
      console.error(err);
      if (err.message === "invalid") {
        runInAction(() => (this.error_message = i18next.t("invalid")));
      }
    }
    return false;
  };

  signUp = async (email: string, password: string): Promise<boolean> => {
    try {
      await this.user_service.create({ email, password });
      await this.login(email, password);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  logout = async (): Promise<boolean> => {
    this.user = null;
    try {
      await client.logout();
      this.routingStore.push(RoutingStore.loginPage);
      client.get("storage").removeItem("feathers-jwt"); // We want to remove this one now;
      sessionStorage.removeItem(Course.last_step_session_storage_key);
    } catch(err) {
      console.error("Failed to logout", err);
    }
    return this.isLoggedIn;
  };

  requestForgotPassword = async (
    email: string
  ): Promise<AccountActionResponse | ForgotPasswordRequestResponse> => {
    try {
      const result = await this.account_service.create({
        email: email,
        action: "forgot_password"
      });
      return result;
    } catch (error) {
      console.error(error);
      return "error";
    }
  };

  resendInvite = async (user: User): Promise<any> => {
    try {
      await this.invite_service.create({
        action: "resend",
        user_id: user.id,
      });
      // toast.success(i18next.t("invite_resent_to", { email: user.email }));
    } catch (error) {
      console.error("Failed to resend invite to user", error);
      // toast.success(i18next.t("failed_to_send_to", { email: user.email }));
      return error;
    }
  };

  acceptInvite = async (token: string, password: string): Promise<any> => {
    try {
      const result = await this.invite_service.patch(null, {
        action: "accept",
        token: token,
        password: password
      });
      return result;
    } catch (error) {
      console.error("Failed to accept invite for user", error);
      return error;
    }
  };

  requestResetPassword = async (token: string, password: string): Promise<AccountActionResponse | ResetPasswordRequestResponse> => {
    try {
      const result = await this.account_service.create({
        token: token,
        password: password,
        action: "reset_password"
      });
      return result;
    } catch (error) {
      console.error(error);
      return "error";
    }
  };

  verifyAccount = async (token: string): Promise<AccountActionResponse> => {
    try {
      const result = await this.account_service.create({
        token: token,
        action: "verify_account"
      });
      return result;
    } catch (error) {
      console.error(error);
      return "error";
    }
  };

  inviteUser = async (user: User): Promise<boolean> => {
    runInAction(() => (this.error_message = null));
    try {
      await user.save();
      return true;
    } catch (error) {
      console.error(error);
      this.error_message = error.message;
      return false;
    }
  };

  submitFeedback = async (feedback: string): Promise<boolean> => {
    try {
      await client.service(Services.Feedback).submit(feedback);
      return true;
    } catch (error) {
      console.error('Error send feedback', error);
      return false;
    }
  }
}
